import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Fade,
  Zoom
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { PaymentService, PaymentUtils } from '../utils/paymentService';
import PaymentRequestCard from './PaymentRequestCard';
import { toast } from 'react-toastify';

const PaymentRequestsList = () => {
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredRequests, setFilteredRequests] = useState([]);

  useEffect(() => {
    fetchPaymentRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [paymentRequests, searchTerm, statusFilter]);

  const fetchPaymentRequests = async () => {
    setLoading(true);
    try {
      const response = await PaymentService.getPaymentRequests();
      if (response && response.data && response.data.status) {
        setPaymentRequests(response.data.data || []);
      } else {
        toast.error(response?.data?.message || 'Failed to fetch payment requests');
        setPaymentRequests([]);
      }
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      toast.error('An error occurred while fetching payment requests');
      setPaymentRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPaymentRequests();
    setRefreshing(false);
  };

  const filterRequests = () => {
    let filtered = paymentRequests;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    setFilteredRequests(filtered);
  };

  const handlePaymentSuccess = (requestId, paymentData) => {
    // Update the payment request status in the local state
    setPaymentRequests(prev => 
      prev.map(request => 
        request.id === requestId 
          ? { ...request, status: 'completed', transaction_id: paymentData.transaction_id }
          : request
      )
    );
    toast.success('Payment completed successfully!');
  };

  const handlePaymentError = (requestId, error) => {
    console.error('Payment error for request:', requestId, error);
    // Optionally update status to failed
    setPaymentRequests(prev => 
      prev.map(request => 
        request.id === requestId 
          ? { ...request, status: 'failed' }
          : request
      )
    );
  };

  const getStats = () => {
    const total = paymentRequests.length;
    const pending = paymentRequests.filter(r => r.status === 'pending').length;
    const completed = paymentRequests.filter(r => r.status === 'completed' || r.status === 'paid').length;
    const totalAmount = paymentRequests.reduce((sum, r) => sum + (r.amount || 0), 0);
    const pendingAmount = paymentRequests
      .filter(r => r.status === 'pending')
      .reduce((sum, r) => sum + (r.amount || 0), 0);

    return { total, pending, completed, totalAmount, pendingAmount };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                Payment Requests
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage and process your payment requests
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>
      </Fade>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={800}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <PaymentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={900}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Payments
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={1000}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.completed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={1100}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <MoneyIcon sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  {PaymentUtils.formatCurrency(stats.pendingAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Amount
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search payment requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Total: ${stats.total}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Pending: ${stats.pending}`}
                color="warning"
                variant="outlined"
              />
              <Chip
                label={`Completed: ${stats.completed}`}
                color="success"
                variant="outlined"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Payment Requests List */}
      {filteredRequests.length === 0 ? (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PaymentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {paymentRequests.length === 0 ? 'No Payment Requests' : 'No Matching Requests'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {paymentRequests.length === 0 
                ? 'You don\'t have any payment requests yet.'
                : 'Try adjusting your search or filter criteria.'
              }
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {filteredRequests.map((request, index) => (
            <Fade in timeout={600 + (index * 100)} key={request.id}>
              <Box>
                <PaymentRequestCard
                  paymentRequest={request}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              </Box>
            </Fade>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default PaymentRequestsList;
